import styled, { keyframes } from 'styled-components';
import { KTick } from './Primitives.styles';

export const CfgPage = styled.div`
	text-align: left;
`;

const rise = keyframes`
	to { transform: none; }
`;

export const CfgHead = styled.div`
	padding: 150px var(--pad) 34px;

	h1 {
		font-size: clamp(46px, 7vw, 104px);
		line-height: 0.94;
	}

	/* homepage-hero row treatment: each line rises out of an overflow clip;
	   the padding/margin pair gives diacritics (Ć, Č) headroom above the
	   0.94 line box so the clip doesn't shave their accents off */
	h1 .row {
		display: block;
		overflow: hidden;
		padding-top: 0.14em;
		margin-top: -0.14em;
	}

	h1 .row span {
		display: inline-block;
		transform: translateY(110%);
		animation: ${rise} 1s cubic-bezier(0.2, 0.65, 0.2, 1) forwards;
	}

	h1 .row:nth-child(2) span {
		animation-delay: 0.12s;
	}

	h1 em {
		font-style: normal;
		color: var(--coral);
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(24px, 2.2vw, 31px);
		display: inline-block;
		margin-top: 10px;
		transform: rotate(-1.4deg);
		animation: cfg-hand 0.7s ease 0.45s backwards;
	}

	@keyframes cfg-hand {
		from {
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		h1 .row span,
		.hand {
			transform: rotate(-1.4deg);
			animation: none;
		}

		h1 .row span {
			transform: none;
		}
	}
`;

export const CfgRow = styled.div<{ $selected?: boolean }>`
	border-top: 1px solid var(--hair);

	&:last-of-type {
		border-bottom: 1px solid var(--hair);
	}

	.head {
		display: grid;
		grid-template-columns: 52px 1fr auto;
		align-items: center;
		gap: clamp(14px, 2.6vw, 36px);
		padding: 24px var(--pad);
		cursor: pointer;
		transition: background 0.2s;
	}

	.head:hover {
		background: rgba(var(--ink-rgb), 0.03);
	}

	h3 {
		font-size: clamp(28px, 3.6vw, 50px);
		color: ${({ $selected }) => ($selected ? 'var(--ink)' : 'var(--mut)')};
		transition: color 0.2s;
	}

	.cavail {
		font-family: var(--fd);
		font-size: 16.5px;
		color: var(--mut);
		white-space: nowrap;
	}

	.cavail.act {
		color: var(--teal-dark);
	}

	.cfg {
		display: ${({ $selected }) => ($selected ? 'block' : 'none')};
		padding: 0 var(--pad) 26px;
	}
`;

export const KModRow = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	margin: 2px 0 16px;
	flex-wrap: wrap;

	@media (max-width: 720px) {
		justify-content: center;
	}
`;

/** Kategorija mode: difficulty chips + stepper on one row. */
export const KKRow = styled.div`
	display: flex;
	align-items: center;
	gap: clamp(14px, 2.4vw, 30px);
	flex-wrap: wrap;
	padding: 12px 0;

	.kchips {
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
	}

	@media (max-width: 720px) {
		flex-direction: column;
		align-items: center;
		text-align: center;
		gap: 12px;

		.kchips {
			justify-content: center;
		}
	}
`;

const subIndent =
	'margin-left: calc(52px + clamp(14px, 2.6vw, 36px) + clamp(28px, 3vw, 56px));';

/** Active subcategory row: name + chips + stepper + remove. */
export const KSRow = styled.div`
	display: grid;
	grid-template-columns: minmax(200px, 1fr) auto auto 40px;
	align-items: center;
	gap: clamp(18px, 2.6vw, 40px);
	padding: 13px 0;
	border-top: 1px dashed rgba(var(--ink-rgb), 0.12);
	${subIndent}

	.sname {
		font-family: var(--fd);
		font-size: clamp(21px, 2.3vw, 30px);
		color: var(--ink);
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	.kchips {
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
	}

	.warn {
		grid-column: 1 / -1;
		font-size: 13.5px;
		font-weight: 600;
		color: var(--coral);
	}

	@media (max-width: 720px) {
		grid-template-columns: 1fr;
		justify-items: center;
		text-align: center;
		gap: 13px;
		padding: 20px 0;
		margin-left: 0;
		position: relative;

		.sname {
			text-align: center;
			max-width: calc(100% - 44px);
		}

		.kchips {
			justify-content: center;
		}

		.krmx {
			position: absolute;
			top: 16px;
			right: 0;
		}
	}
`;

/** Inactive subcategory row: name + add button, no counts shown. */
export const KAvRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: clamp(18px, 2.6vw, 40px);
	padding: 12px 0;
	border-top: 1px dashed rgba(var(--ink-rgb), 0.1);
	${subIndent}

	.sname {
		font-family: var(--fd);
		font-size: clamp(19px, 2.1vw, 27px);
		color: var(--mut);
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	@media (max-width: 720px) {
		flex-direction: column;
		justify-content: center;
		gap: 10px;
		padding: 16px 0;
		text-align: center;
		margin-left: 0;
	}
`;

export const KAddBtn = styled.button`
	font-family: var(--fd);
	font-size: 15px;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	padding: 10px 20px;
	border-radius: var(--r-pill);
	border: 1.5px solid var(--teal-dark);
	color: var(--teal-dark);
	background: transparent;
	cursor: pointer;
	transition: 0.15s;
	white-space: nowrap;

	&:hover {
		background: var(--teal);
		border-color: var(--teal);
		color: var(--teal-deep);
	}
`;

export const KRmx = styled.button`
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: 1.5px solid rgba(var(--ink-rgb), 0.25);
	background: none;
	color: var(--mut);
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: 0.15s;

	&:hover {
		border-color: var(--coral);
		color: var(--coral);
	}

	svg {
		pointer-events: none;
	}
`;

export const KCkRow = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 11px;
	cursor: pointer;
	user-select: none;

	.t {
		font-size: 15.5px;
		font-weight: 600;
		color: var(--brown);
	}

	/* mobile: quieter and smaller so the CTA next to it stays the star */
	@media (max-width: 720px) {
		gap: 8px;

		.t {
			font-size: 13px;
			color: var(--mut);
		}

		${KTick} {
			width: 22px;
			height: 22px;
			border-width: 1.5px;
		}

		${KTick} svg {
			width: 12px;
			height: 12px;
		}
	}
`;
